import {
    Bundle,
    BundleSubscription,
    Group,
    ManagedSubscription,
    PriceBand,
    RenewalPeriod,
    SubscriptionResponse,
    Tier,
    TierSubscription,
    UserSubscription,
} from '@easeltv/client-sdk';
import { is } from '@easeltv/utilities';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '../../providers/AuthProvider/AuthProvider';
import { useApi } from '../useApi/useApi';

/* ---------------------------------- Types --------------------------------- */

export type GroupTier = Tier & {
    type: 'tier';
    purchased: boolean;
    priceBands: {
        [k in RenewalPeriod]?: PriceBand;
    };
};

export type GroupBundle = Bundle & {
    type: 'bundle';
    purchased: boolean;
    priceBands: {
        [k in RenewalPeriod]?: PriceBand;
    };
};

export type GroupChild = GroupTier | GroupBundle;

export interface ResolvedGroup extends Omit<Group, 'children'> {
    children: GroupChild[];
}

export interface SubscriptionData extends Omit<SubscriptionResponse, 'groups'> {
    groups: ResolvedGroup[];
}

/* --------------------------------- Helpers -------------------------------- */

export const isManagedSubscription = (
    x: UserSubscription,
): x is ManagedSubscription => x.managed;

export const isTierSubscription = (
    x: UserSubscription,
): x is TierSubscription => isManagedSubscription(x) && 'tierId' in x;

/**
 * resolveTier
 *
 * Inject appropriate price bands into tier object
 *
 * @param x - Tier object
 * @param priceBands - Price bands to resolve from
 * @returns
 */
export const resolveTier = (
    x: Tier | undefined,
    priceBands: PriceBand[],
    existingSubscriptions: UserSubscription[],
): GroupTier | undefined => {
    if (!x) return undefined;

    return {
        ...x,
        type: 'tier',
        purchased: existingSubscriptions
            .filter(isTierSubscription)
            .filter((s) => s.status !== 'cancelled')
            .some((sub) => sub.tierId === x.id),
        priceBands: {
            daily: x.availabilityWindows.priceBands.daily
                ? priceBands.find(
                      (priceBand) =>
                          priceBand.id ===
                          x.availabilityWindows.priceBands.daily,
                  )
                : undefined,
            weekly: x.availabilityWindows.priceBands.weekly
                ? priceBands.find(
                      (priceBand) =>
                          priceBand.id ===
                          x.availabilityWindows.priceBands.weekly,
                  )
                : undefined,
            monthly: x.availabilityWindows.priceBands.monthly
                ? priceBands.find(
                      (priceBand) =>
                          priceBand.id ===
                          x.availabilityWindows.priceBands.monthly,
                  )
                : undefined,
            yearly: x.availabilityWindows.priceBands.yearly
                ? priceBands.find(
                      (priceBand) =>
                          priceBand.id ===
                          x.availabilityWindows.priceBands.yearly,
                  )
                : undefined,
        },
    };
};

export const isBundleSubscription = (
    x: UserSubscription,
): x is BundleSubscription => isManagedSubscription(x) && 'bundleId' in x;

/**
 * resolveBundle
 *
 * Inject appropriate price bands into bundle object
 *
 * @param x - Bundle object
 * @param priceBands - Price bands to resolve from
 * @returns
 */
export const resolveBundle = (
    x: Bundle | undefined,
    priceBands: PriceBand[],
    existingSubscriptions: UserSubscription[],
): GroupBundle | undefined => {
    if (!x) return undefined;

    return {
        ...x,
        type: 'bundle',
        purchased: existingSubscriptions
            .filter(isBundleSubscription)
            .filter((s) => s.status !== 'cancelled')
            .some((sub) => sub.bundleId === x.id),
        priceBands: {
            daily: x.availabilityWindows.priceBands.daily
                ? priceBands.find(
                      (priceBand) =>
                          priceBand.id ===
                          x.availabilityWindows.priceBands.daily,
                  )
                : undefined,
            weekly: x.availabilityWindows.priceBands.weekly
                ? priceBands.find(
                      (priceBand) =>
                          priceBand.id ===
                          x.availabilityWindows.priceBands.weekly,
                  )
                : undefined,
            monthly: x.availabilityWindows.priceBands.monthly
                ? priceBands.find(
                      (priceBand) =>
                          priceBand.id ===
                          x.availabilityWindows.priceBands.monthly,
                  )
                : undefined,
            yearly: x.availabilityWindows.priceBands.yearly
                ? priceBands.find(
                      (priceBand) =>
                          priceBand.id ===
                          x.availabilityWindows.priceBands.yearly,
                  )
                : undefined,
        },
    };
};

/**
 * resolveGroup
 *
 * Fit group object with the appropriate tiers, bundles and price bands
 *
 * @param group - Group to resolve
 * @param All tiers bundles and price bands to resolve from
 * @returns
 */
const resolveGroup = (
    group: Group,
    {
        tiers,
        bundles,
        priceBands,
        existingSubscriptions,
    }: {
        tiers: Tier[];
        bundles: Bundle[];
        priceBands: PriceBand[];
        existingSubscriptions: UserSubscription[];
    },
): ResolvedGroup => ({
    ...group,
    children: group.children
        .map((child): GroupChild | undefined => {
            const [type, id] = child.split('_');

            if (type === 'bundle')
                return resolveBundle(
                    bundles.find((b) => b.id === id),
                    priceBands,
                    existingSubscriptions,
                );
            if (type === 'tier')
                return resolveTier(
                    tiers.find((b) => b.id === id),
                    priceBands,
                    existingSubscriptions,
                );

            return undefined;
        })
        .filter(is.notUndefined),
});

/* ---------------------------------- Hook ---------------------------------- */

/**
 * useSubscription
 *
 * @returns Object containing Subscription details
 */
export function useSubscription() {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<SubscriptionData | null>(null);
    const { user } = useAuth();

    const existingSubscriptions = useMemo(
        () => user?.extend?.subscriptions ?? [],
        [user?.extend?.subscriptions],
    );

    const api = useApi();

    const request = useRef(async () => {
        setLoading(true);

        const result = await api.getSubscription({ extend: ['price-band'] });

        if (!result.ok) {
            // @TODO: Error handling
            // eslint-disable-next-line no-console
            console.trace(result);
            return;
        }

        const { body: subscriptionData } = result.payload;

        const priceBands = subscriptionData.extend?.priceBands ?? [];
        const { bundles, tiers } = subscriptionData;

        const groups = subscriptionData.groups.map((group) =>
            resolveGroup(group, {
                priceBands,
                bundles,
                tiers,
                existingSubscriptions,
            }),
        );

        setData({
            ...subscriptionData,
            groups,
        });
        setLoading(false);
    });

    useEffect(() => {
        if (request.current) {
            request.current();
        }
    }, []);

    return { loading, data };
}
